/**
 * Atlas PM · 用户管理 · 弹窗（P4.3）
 *
 *   UserFormDialog        新建 / 编辑（编辑时 username 置灰不可改）
 *   StatusConfirmDialog   启用 / 停用二次确认
 *   ResetPasswordDialog   重置口令二次确认（输入新口令）
 *
 * ⚠️ 颜色一律走 tokens.css 变量；表单控件用本特性的 .p43-* 类。
 */

import { useEffect, useState, type ReactNode } from 'react';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import { ApiError } from '../../../api/client';
import { type OrgNode, type RoleItem, type UserItem } from './api';
import {
  useCreateUser,
  useResetUserPassword,
  useSetUserStatus,
  useUpdateUser,
} from './useUsers';
import { OrgCheckList } from './OrgTreeSelect';

/* ── 本地字段容器（自包含样式，不依赖 project.css） ── */
function Field({
  label,
  required,
  error,
  hint,
  htmlFor,
  className,
  children,
}: {
  label: ReactNode;
  required?: boolean;
  error?: string | null;
  hint?: ReactNode;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={['p43-field', error ? 'is-error' : '', className].filter(Boolean).join(' ')}>
      <label className="p43-field__label" htmlFor={htmlFor}>
        {label}
        {required && <em className="p43-field__req">*</em>}
      </label>
      {children}
      {hint && <div className="p43-field__hint">{hint}</div>}
      {error && <div className="p43-field__err">{error}</div>}
    </div>
  );
}

function errText(err: unknown): string | null {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return null;
}

/* ═══════════════════════════════════════════════════════════
   新建 / 编辑
   ═══════════════════════════════════════════════════════════ */

export interface UserFormDialogProps {
  open: boolean;
  /** create 新建 / edit 编辑 */
  mode: 'create' | 'edit';
  user: UserItem | null;
  orgs: OrgNode[];
  roles: RoleItem[];
  onClose(): void;
  onSaved(message: string): void;
}

export function UserFormDialog({
  open,
  mode,
  user,
  orgs,
  roles,
  onClose,
  onSaved,
}: UserFormDialogProps) {
  const isEdit = mode === 'edit';
  const createMut = useCreateUser();
  const editMut = useUpdateUser(user?.id ?? '');
  const mutation = isEdit ? editMut : createMut;

  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [orgIds, setOrgIds] = useState<string[]>([]);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [formErr, setFormErr] = useState<string | null>(null);

  // ⭐ P6.39：按已选角色动态决定字段可见性。
  //   优先级 SUPER_ADMIN > PROVINCE_ADMIN > OPERATOR：
  //     含超管 → 不显示组织；含省管理员（且无超管）→ 显示所属组织（必选）；
  //     仅操作员 → 不显示组织。
  //   数据范围一律由角色决定，前端不再暴露选择入口。
  const selectedCodes = roles.filter((r) => roleIds.includes(r.id)).map((r) => r.code);
  const hasSuper = selectedCodes.includes('SUPER_ADMIN');
  const hasProvince = selectedCodes.includes('PROVINCE_ADMIN');
  const showOrgPicker = hasProvince && !hasSuper;

  // 打开 / 切换对象时重置表单
  useEffect(() => {
    if (!open) return;
    setFormErr(null);
    if (isEdit && user) {
      setUsername(user.username);
      setDisplayName(user.displayName);
      setPassword('');
      // ⭐ P7.4：优先回填多组织；存量用户 scopeOrgIds 为空则回退为 [orgId]，
      //   保证编辑存量省管理员时不丢组织。
      setOrgIds(
        user.scopeOrgIds.length > 0
          ? user.scopeOrgIds
          : user.orgId
            ? [user.orgId]
            : [],
      );
      setRoleIds(user.roles.map((r) => r.roleId));
    } else {
      setUsername('');
      setDisplayName('');
      setPassword('');
      setOrgIds([]);
      setRoleIds([]);
    }
  }, [open, isEdit, user]);

  const toggleRole = (id: string) => {
    setRoleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleOrg = (id: string) => {
    setOrgIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = () => {
    setFormErr(null);
    const name = username.trim();
    const display = displayName.trim();

    if (!isEdit) {
      if (!name) return setFormErr('请填写用户名');
      if (!/^[A-Za-z0-9_.-]{2,64}$/.test(name)) {
        return setFormErr('用户名仅支持字母/数字/下划线/点/连字符，2-64 位');
      }
    }
    if (!display) return setFormErr('请填写姓名');
    if (!isEdit && password.length < 6) return setFormErr('初始口令至少 6 位');
    if (roleIds.length === 0) return setFormErr('至少选择一个角色');
    if (showOrgPicker && orgIds.length === 0) return setFormErr('请选择所属组织');

    // ⭐ P7.4：省管理员「所属组织」可多选。
    //   orgId      = 首个组织（主归属，后端 org_id 非空 / 省管理员校验依赖它）
    //   scopeOrgIds = 全部所选（写入 user_role.scope_org_ids，可管多个省）
    //   操作员 / 超管不发组织字段，由后端按建号者组织兜底。
    const orgPart =
      showOrgPicker && orgIds.length > 0
        ? { orgId: orgIds[0], scopeOrgIds: orgIds }
        : {};

    if (isEdit) {
      editMut.mutate(
        { displayName: display, roleIds, ...orgPart },
        {
          onSuccess: () => {
            onSaved(`已保存用户「${display}」`);
            onClose();
          },
          onError: (e) => setFormErr(errText(e) ?? '保存失败'),
        },
      );
    } else {
      createMut.mutate(
        { username: name, displayName: display, password, roleIds, ...orgPart },
        {
          onSuccess: () => {
            onSaved(`已创建用户「${name}」`);
            onClose();
          },
          onError: (e) => setFormErr(errText(e) ?? '创建失败'),
        },
      );
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? `编辑用户 · ${user?.username ?? ''}` : '新建用户'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            取消
          </Button>
          <Button variant="primary" onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? '提交中…' : isEdit ? '保存' : '创建'}
          </Button>
        </>
      }
    >
      {formErr && <div className="p43-formerr">⚠ {formErr}</div>}

      <div className="p43-formgrid">
        <Field label="用户名" required={!isEdit} htmlFor="p43-username">
          <input
            id="p43-username"
            className="p43-input"
            value={username}
            disabled={isEdit}
            placeholder={isEdit ? '用户名创建后不可修改' : '如 zhangsan'}
            autoComplete="off"
            onChange={(e) => setUsername(e.target.value)}
          />
        </Field>

        <Field label="姓名" required htmlFor="p43-displayname">
          <input
            id="p43-displayname"
            className="p43-input"
            value={displayName}
            placeholder="如 张三"
            autoComplete="off"
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>

        {!isEdit && (
          <Field label="初始口令" required hint="至少 6 位" htmlFor="p43-password">
            <input
              id="p43-password"
              className="p43-input"
              type="password"
              value={password}
              placeholder="至少 6 位"
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        )}

        {showOrgPicker && (
          <Field
            label="所属组织"
            required
            htmlFor="p43-org"
            className="p43-field--full"
            hint="可多选；省管理员将能管理所选省份的数据"
          >
            <OrgCheckList
              id="p43-org"
              orgs={orgs}
              selected={orgIds}
              onToggle={toggleOrg}
              disabled={mutation.isPending}
            />
          </Field>
        )}

        <div className="p43-field p43-field--full">
          <label className="p43-field__label">
            角色
            <em className="p43-field__req">*</em>
          </label>
          <div className="p43-roles">
            {roles.map((r) => {
              const checked = roleIds.includes(r.id);
              return (
                <label key={r.id} className={['p43-roletag', checked ? 'on' : ''].join(' ')}>
                  <input
                    type="checkbox"
                    className="p43-rolecheck"
                    data-role-id={r.id}
                    data-role-code={r.code}
                    checked={checked}
                    onChange={() => toggleRole(r.id)}
                  />
                  <span>{r.name}</span>
                  <span className="p43-roletag__code">{r.code}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════
   启用 / 停用 二次确认
   ═══════════════════════════════════════════════════════════ */

export interface StatusConfirmDialogProps {
  open: boolean;
  user: UserItem | null;
  onClose(): void;
  onDone(message: string): void;
}

export function StatusConfirmDialog({ open, user, onClose, onDone }: StatusConfirmDialogProps) {
  const mut = useSetUserStatus();
  const disabling = user?.status === 1;
  const targetStatus = disabling ? 0 : 1;

  const confirm = () => {
    if (!user) return;
    mut.mutate(
      { id: user.id, status: targetStatus },
      {
        onSuccess: () => {
          onDone(`已${disabling ? '停用' : '启用'}用户「${user.displayName}」`);
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      size="sm"
      danger={disabling}
      title={disabling ? '停用用户' : '启用用户'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
            取消
          </Button>
          <Button
            variant={disabling ? 'danger' : 'primary'}
            onClick={confirm}
            disabled={mut.isPending}
          >
            {mut.isPending ? '处理中…' : `确认${disabling ? '停用' : '启用'}`}
          </Button>
        </>
      }
    >
      <div className="p43-confirm">
        确定要{disabling ? '停用' : '启用'}用户 <b>{user?.displayName}</b>（
        <span className="p43-mono">{user?.username}</span>）吗？
      </div>
      <div className="p43-confirm__hint">
        {disabling
          ? '停用后该账号将无法登录，已登录会话在下次校验时失效。'
          : '启用后该账号可正常登录并按角色权限访问数据。'}
      </div>
      {errText(mut.error) && <div className="p43-formerr">⚠ {errText(mut.error)}</div>}
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════
   重置口令 二次确认
   ═══════════════════════════════════════════════════════════ */

export interface ResetPasswordDialogProps {
  open: boolean;
  user: UserItem | null;
  onClose(): void;
  onDone(message: string): void;
}

export function ResetPasswordDialog({ open, user, onClose, onDone }: ResetPasswordDialogProps) {
  const mut = useResetUserPassword();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [formErr, setFormErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      setConfirm('');
      setFormErr(null);
    }
  }, [open, user]);

  const submit = () => {
    setFormErr(null);
    if (password.length < 6) return setFormErr('新口令至少 6 位');
    if (password !== confirm) return setFormErr('两次输入的口令不一致');
    if (!user) return;
    mut.mutate(
      { id: user.id, password },
      {
        onSuccess: () => {
          onDone(`已重置用户「${user.displayName}」的登录口令`);
          onClose();
        },
        onError: (e) => setFormErr(errText(e) ?? '重置失败'),
      },
    );
  };

  return (
    <Modal
      open={open}
      size="sm"
      title={`重置口令 · ${user?.username ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
            取消
          </Button>
          <Button variant="primary" onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? '提交中…' : '确认重置'}
          </Button>
        </>
      }
    >
      {formErr && <div className="p43-formerr">⚠ {formErr}</div>}
      <div className="p43-confirm__hint">
        将为 <b>{user?.displayName}</b> 设置一个新的登录口令，旧口令立即失效。
      </div>
      <Field label="新口令" required hint="至少 6 位">
        <input
          className="p43-input"
          type="password"
          data-testid="p43-reset-password"
          value={password}
          autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      <Field label="确认口令" required>
        <input
          className="p43-input"
          type="password"
          value={confirm}
          autoComplete="new-password"
          onChange={(e) => setConfirm(e.target.value)}
        />
      </Field>
    </Modal>
  );
}
